/**
 * DataDog RUM (Real User Monitoring) Utilities
 *
 * Centralized utilities for DataDog RUM integration including:
 * - User identification
 * - Custom action tracking
 * - Error reporting
 * - Context management
 */

import { datadogRum } from "@datadog/browser-rum";

/**
 * Check if RUM is initialized and available
 */
export const isRumInitialized = (): boolean => {
  try {
    return typeof datadogRum !== "undefined" && !!datadogRum.getInternalContext;
  } catch {
    return false;
  }
};

/**
 * Set user information in RUM for tracking authenticated users
 *
 * @param userId - Unique user identifier
 * @param email - User email (optional)
 * @param name - User name (optional)
 */
export const setRumUser = (
  userId: string,
  email?: string,
  name?: string,
): void => {
  if (!isRumInitialized()) {
    console.warn("DataDog RUM not initialized, skipping setUser");
    return;
  }

  try {
    datadogRum.setUser({
      id: userId ?? "-1",
      ...(email && { email }),
      ...(name && { name }),
    });
  } catch (error) {
    console.error("Failed to set RUM user:", error);
  }
};

/**
 * Clear user information from RUM (e.g., on logout)
 */
export const clearRumUser = (): void => {
  if (!isRumInitialized()) {
    return;
  }

  try {
    datadogRum.clearUser();
  } catch (error) {
    console.error("Failed to clear RUM user:", error);
  }
};

/**
 * Track a custom user action in RUM
 *
 * @param name - Action name (e.g., "checkout_completed", "video_played")
 * @param context - Additional context data
 */
export const trackRumAction = (
  name: string,
  context?: Record<string, any>,
): void => {
  if (!isRumInitialized()) {
    console.warn("DataDog RUM not initialized, skipping trackAction");
    return;
  }

  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.error("Failed to track RUM action:", error);
  }
};

/**
 * Report an error to RUM with optional context
 *
 * @param error - Error object or message
 * @param context - Additional context data
 */
export const reportRumError = (
  error: Error | string | unknown,
  context?: Record<string, any>,
): void => {
  if (!isRumInitialized()) {
    console.warn("DataDog RUM not initialized, skipping error report");
    return;
  }

  try {
    // Convert to Error object if it's a string
    const errorObj =
      typeof error === "string" ? new Error(error) : (error as Error);

    // Add context if provided
    if (context) {
      datadogRum.addError(errorObj, context);
    } else {
      datadogRum.addError(errorObj);
    }
  } catch (err) {
    console.error("Failed to report RUM error:", err);
  }
};

/**
 * Add global context to all RUM events
 *
 * @param key - Context key
 * @param value - Context value
 */
export const addRumContext = (key: string, value: any): void => {
  if (!isRumInitialized()) {
    console.warn("DataDog RUM not initialized, skipping addContext");
    return;
  }

  try {
    datadogRum.setGlobalContextProperty(key, value);
  } catch (error) {
    console.error("Failed to add RUM context:", error);
  }
};

/**
 * Remove global context property
 *
 * @param key - Context key to remove
 */
export const removeRumContext = (key: string): void => {
  if (!isRumInitialized()) {
    return;
  }

  try {
    datadogRum.removeGlobalContextProperty(key);
  } catch (error) {
    console.error("Failed to remove RUM context:", error);
  }
};

/**
 * Start a custom timing measurement
 *
 * @param name - Timing name
 * @returns Function to stop the timing
 */
export const startRumTiming = (name: string): (() => void) => {
  const startTime = performance.now();

  return () => {
    if (!isRumInitialized()) {
      return;
    }

    try {
      const duration = performance.now() - startTime;
      datadogRum.addAction(name, { duration });
    } catch (error) {
      console.error("Failed to record RUM timing:", error);
    }
  };
};

/**
 * Add a feature flag to RUM context
 *
 * @param flagName - Feature flag name
 * @param flagValue - Feature flag value
 */
export const addRumFeatureFlag = (flagName: string, flagValue: any): void => {
  if (!isRumInitialized()) {
    return;
  }

  try {
    datadogRum.addFeatureFlagEvaluation(flagName, flagValue);
  } catch (error) {
    console.error("Failed to add RUM feature flag:", error);
  }
};
